/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RaiseVehicleTickets.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Button, DialogActions, Grid, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import { componentStyle } from "../../../config/appTheme";
import RaiseTicketTemplate from "./RaiseTicketTemplate";
import SelectTemplate from "./SelectDialog/SelectTemplate";
import { useDispatch, useSelector } from "react-redux";
import DialogIndex from "./SelectDialog/DialogIndex";
import KeyValueDisplayCard from "./SelectDialog/KeyValueDisplayCard";
import CustomCheckBoxWithoutFormControl from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxWithoutFormControl";
import RaiseTicketRightTemplate from "./RaiseTicketRightTemplate";
import { CAPABILITY_CODE } from "../../../config/appConstants";
import VehicleSavedLinkageCard from "./VehicleSavedLinkageCard";
import CustomToolTip from "../../../components/infoButton/CustomToolTip";
import {
  adminSettingsAPI,
  HandleApiActions,
  labCar,
  legalRequirements,
  userManagementApis,
} from "../../../redux/actions";
import softwareIntersectionAPI from "../../../redux/actions/softwareIntersectionActions";
import { ENTITY } from "../../../components/customComponentsV2/SmartMatchingDialogContainer/constants/entityTypes";
import AISuggestionDialogContainer from "../../../components/customComponentsV2/SmartMatchingDialogContainer/AiSuggestionDialogContainer";
import testCasesActions from "../../../redux/actions/testCasesActions";
import TestCaseCampaignLinkDialog from "./TestCaseCampaignLinkDialog";
import TestCaseComponentLinkDialog from "./TestCaseComponentLinkDialog";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import SelectDialogActionButtons from "./SelectDialog/SelectDialogActionButtons";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";

const VEHICLE_COMPONENT_ENTITY_TYPE = "VC";
const VEHICLE_SYSTEM_ENTITY_TYPE = "VS";

const replaceTestCaseIds = (testCases, idReplacementMap) => {
  if (!Array.isArray(testCases)) return testCases;
  return testCases.map((tc) => {
    const newId = idReplacementMap.get(String(tc?.test_case_id));
    return newId ? { ...tc, test_case_id: newId } : tc;
  });
};

const replaceIdInArray = (ids, idReplacementMap) => {
  if (!Array.isArray(ids)) return ids;
  return ids.map((id) => idReplacementMap.get(String(id)) ?? id);
};

const updateLegalItemWithNewIds = (legalItem, idReplacementMap) => {
  const updatedLegal = { ...legalItem };
  if (Array.isArray(updatedLegal.test_case_ids)) {
    updatedLegal.test_case_ids = replaceIdInArray(
      updatedLegal.test_case_ids,
      idReplacementMap,
    );
  }
  if (Array.isArray(updatedLegal.test_cases)) {
    updatedLegal.test_cases = replaceTestCaseIds(
      updatedLegal.test_cases,
      idReplacementMap,
    );
  }
  return updatedLegal;
};

const updateLinkageWithNewIds = (linkage, idReplacementMap) => {
  const updatedLinkage = { ...linkage };

  if (Array.isArray(updatedLinkage.test_case_ids)) {
    updatedLinkage.test_case_ids = replaceIdInArray(
      updatedLinkage.test_case_ids,
      idReplacementMap,
    );
  }

  if (Array.isArray(updatedLinkage.test_cases)) {
    updatedLinkage.test_cases = replaceTestCaseIds(
      updatedLinkage.test_cases,
      idReplacementMap,
    );
  }

  if (Array.isArray(updatedLinkage.legal)) {
    updatedLinkage.legal = updatedLinkage.legal.map((legalItem) =>
      updateLegalItemWithNewIds(legalItem, idReplacementMap),
    );
  }

  return updatedLinkage;
};

function RaiseVehicleTickets({
  savedLinkages,
  setSavedLinkages,
  entityType,
  vehicleTicketMode = "component",
}) {
  const dispatch = useDispatch();
  const isVehicleSystemMode = vehicleTicketMode === "system";
  const vehicleEntityType = isVehicleSystemMode
    ? VEHICLE_SYSTEM_ENTITY_TYPE
    : VEHICLE_COMPONENT_ENTITY_TYPE;
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const [openDuplicateAlert, setOpenDuplicateAlert] = useState(false);
  const [duplicateAlertMessage, setDuplicateAlertMessage] = useState("");
  const [zoneList, setZoneList] = useState([]);
  const [isZonalProject, setIsZonalProject] = useState(false);

  const [selectedVSMain, setSelectedVSMain] = useState([]);
  const [selectedVSForDisplay, setSelectedVSForDisplay] = useState([]);
  const [selectedVCMain, setSelectedVCMain] = useState([]);
  const [openEESelect, setOpenEESelect] = useState(false);

  const [selectedLegalMain, setSelectedLegalMain] = useState([]);
  const [selectedLegalForDisplay, setSelectedLegalForDisplay] = useState([]);
  const [openLegalSelect, setOpenLegalSelect] = useState(false);
  const [disabledVSComponents, setDisabledVSComponents] = useState([]);

  const [openTestCaseSelect, setOpenTestCaseSelect] = useState(false);
  const [selectedTestCaseForDisplay, setSelectedTestCaseForDisplay] =
    useState(null);
  const [selectedTestCaseMain, setSelectedTestCaseMain] = useState(null);
  const [selectedComponentRequestor, setSelectedComponentRequestor] = useState(
    [],
  );
  const [selectedLegalTechUserDetails, setSelectedLegalTechUserDetails] =
    useState([]);

  const [selectedComplianceRequestors, setSelectedComplianceRequestors] =
    useState([]);
  const [selectedComplianceReviewers, setSelectedComplianceReviewers] =
    useState([]);
  const [selectedComplianceSupplier, setSelectedComplianceSupplier] = useState(
    [],
  );
  const [description, setDescription] = useState("");
  const [
    selectedComponentSupplierDetails,
    setSelectedComponentSupplierDetails,
  ] = useState([]);
  const [selectedDynamicForm, setSelectedDynamicForm] = useState(null);

  const [dynamicFormFilteredData, setDynamicFormFilteredData] = useState(null);
  const [showDynamicSelect, setShowDynamicSelect] = useState(false);
  const [disabledLegalRequirement, setDisabledLegalRequirement] = useState([]);
  const [allUsersList, setAllUsersList] = useState([]);
  const [openAISuggestion, setOpenAISuggestion] = useState(false);
  const [showSmartMatchDialog, setshowSmartMatchDialog] = useState(false);
  const [componentToBeMatched, setComponentToBeMatched] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [
    selectedSmartMatchTestCaseSuggestions,
    setSelectedSmartMatchTestCaseSuggestions,
  ] = useState([]);
  const [disableSmartMatch, setDisableSmartMatch] = useState(false);
  const [closeAiDialogOnAdd, setCloseAiDialogOnAdd] = useState(false);
  const [openCampaignLinkDialog, setOpenCampaignLinkDialog] = useState(false);
  const [testCasesToCampaignLink, setTestCasesToCampaignLink] = useState([]);
  const [openComponentLinkDialog, setOpenComponentLinkDialog] = useState(false);
  const [testCasesToComponentLink, setTestCasesToComponentLink] = useState([]);
  const [formResetKey, setFormResetKey] = useState(0);
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [pendingVehicleComponent, setPendingVehicleComponent] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [pendingTargetType, setPendingTargetType] = useState("");
  const adminSettingsDetails = useSelector(
    (state) => state?.adminSettingsReducer,
  );
  const [ecuSuppliersList, setEcuSuppliersList] = useState([]);
  const savedLinkagesRef = React.useRef(savedLinkages);

  useEffect(() => {
    savedLinkagesRef.current = savedLinkages;
  }, [savedLinkages]);

  const getPrimarySelectedVC = () => selectedVCMain?.[0] || null;
  const getPrimarySelectedVehicleEntity = () =>
    isVehicleSystemMode ? selectedVSMain?.[0] || null : getPrimarySelectedVC();
  const getVehicleSystemId = (vehicleSystem) =>
    Number(vehicleSystem?.id || vehicleSystem?.vs_id);
  const getVehicleSystemLinkedLegalIds = (vehicleSystem) =>
    new Set(
      (vehicleSystem?.legal_req_ids || [])
        .map(Number)
        .filter((lrId) => Number.isFinite(lrId)),
    );
  const isVehicleSystemLinkedWithLegalRequirement = (
    vehicleSystem,
    lrId = selectedLegalMain?.lr_id,
  ) => {
    const legalRequirementId = Number(lrId);
    if (!Number.isFinite(legalRequirementId)) return false;

    const vehicleSystemId = getVehicleSystemId(vehicleSystem);
    return (
      getVehicleSystemLinkedLegalIds(vehicleSystem).has(legalRequirementId) ||
      disabledVSComponents.includes(vehicleSystemId)
    );
  };
  const getSelectedVehicleSystemLinkedCount = (lrId) => {
    const legalRequirementId = Number(lrId);
    if (!isVehicleSystemMode || !Number.isFinite(legalRequirementId)) return 0;

    return selectedVSMain.filter((vehicleSystem) =>
      getVehicleSystemLinkedLegalIds(vehicleSystem).has(legalRequirementId),
    ).length;
  };
  const shouldShowLegalRequirementLinkedInfo = (lrId) => {
    const linkedCount = getSelectedVehicleSystemLinkedCount(lrId);
    return linkedCount > 0 && linkedCount < selectedVSMain.length;
  };

  const clearCampaignLinkFields = useCallback(() => {
    setOpenCampaignLinkDialog(false);
    setTestCasesToCampaignLink([]);
  }, []);

  const clearForm = useCallback(() => {
    setFormResetKey((k) => k + 1);
    setSelectedVSMain([]);
    setSelectedVSForDisplay([]);
    setSelectedVCMain([]);
    setSelectedLegalMain([]);
    setSelectedLegalForDisplay([]);
    setSelectedDynamicForm(null);
    setSelectedComplianceRequestors([]);
    setSelectedComplianceReviewers([]);
    setDescription("");
    setSelectedTestCaseForDisplay([]);
    setSelectedTestCaseMain([]);
    setSelectedComponentSupplierDetails([]);
    setSelectedComplianceSupplier([]);
    setDisabledVSComponents([]);
    setSelectedSmartMatchTestCaseSuggestions([]);
    clearCampaignLinkFields();
  }, [clearCampaignLinkFields]);

  const addFullyLinkedTestCases = (testCases = []) => {
    if (!Array.isArray(testCases) || testCases.length === 0) return;

    const idReplacementMap = new Map();
    for (const testCase of testCases) {
      const oldId = testCase?.original_test_case_id || testCase?.test_case_id;
      const newId = testCase?.new_test_case_id || testCase?.test_case_id;
      if (oldId && newId && String(oldId) !== String(newId)) {
        idReplacementMap.set(String(oldId), String(newId));
      }
    }

    if (idReplacementMap.size > 0) {
      const updatedLinkages = (savedLinkagesRef.current || []).map((linkage) =>
        updateLinkageWithNewIds(linkage, idReplacementMap),
      );

      setSavedLinkages(updatedLinkages);
    }

    setSelectedTestCaseMain((prev) => {
      const currentSelection = Array.isArray(prev) ? prev : [];
      const mergedSelection = [...currentSelection];

      for (const testCase of testCases) {
        const resolvedId = testCase?.new_test_case_id ?? testCase?.test_case_id;
        const alreadySelected = mergedSelection.some(
          (selectedItem) =>
            String(selectedItem?.test_case_id) === String(resolvedId) ||
            (testCase?.original_test_case_id && String(selectedItem?.test_case_id) === String(testCase.original_test_case_id))
        );
        
        if (alreadySelected) {
          const index = mergedSelection.findIndex(
            (item) => String(item?.test_case_id) === String(testCase?.original_test_case_id || testCase?.test_case_id)
          );
          if (index !== -1) {
            mergedSelection[index] = {
              ...mergedSelection[index],
              ...testCase,
              original_test_case_id: testCase?.original_test_case_id || testCase?.test_case_id,
              test_case_id: resolvedId,
              is_campaign_linked: true,
              is_component_linked: true,
              is_lr_linked: true,
            };
          }
        } else {
          mergedSelection.push({
            ...testCase,
            original_test_case_id: testCase?.original_test_case_id || testCase?.test_case_id,
            test_case_id: resolvedId,
            is_campaign_linked: true,
            is_component_linked: true,
            is_lr_linked: true,
          });
        }
      }

      setSelectedTestCaseForDisplay(mergedSelection);
      setSelectedSmartMatchTestCaseSuggestions(mergedSelection);
      return mergedSelection;
    });
  };

  const openCampaignDialogForTestCases = (testCases = []) => {
    setTestCasesToCampaignLink(testCases);
    setOpenCampaignLinkDialog(true);
  };

  const pipelineQueueRef = React.useRef([]);

  const runPipelineStep3 = (testCases = []) => {
    if (!Array.isArray(testCases) || testCases.length === 0) return;
    addFullyLinkedTestCases(
      testCases.map((tc) => ({
        ...tc,
        is_campaign_linked: true,
        is_component_linked: true,
        is_lr_linked: true,
      })),
    );
  };

  const runPipelineStep2 = (testCases = []) => {
    if (!Array.isArray(testCases) || testCases.length === 0) return;

    const updatedTestCases = testCases.map(tc => {
      if (tc?.new_test_case_id && String(tc.new_test_case_id) !== String(tc.test_case_id)) {
        return {
          ...tc,
          original_test_case_id: tc.test_case_id,
          test_case_id: tc.new_test_case_id
        };
      }
      return tc;
    });

    const campaignUnlinked = updatedTestCases.filter(
      (tc) => tc?.is_campaign_linked === false,
    );
    const campaignLinked = updatedTestCases.filter(
      (tc) => tc?.is_campaign_linked !== false,
    );

    if (campaignLinked.length > 0) {
      runPipelineStep3(campaignLinked);
    }

    if (campaignUnlinked.length > 0) {
      setTestCasesToCampaignLink(campaignUnlinked);
      setOpenCampaignLinkDialog(true);
    }
  };

  const handleSelectedTestCases = (testCases = []) => {
    const currentSelectedIds = new Set(
      (selectedTestCaseMain || []).map((tc) => tc?.test_case_id),
    );

    const freshTestCases = testCases.filter(
      (tc) => !currentSelectedIds.has(tc?.test_case_id),
    );

    if (freshTestCases.length === 0) return;

    const componentUnlinked = freshTestCases.filter(
      (tc) =>
        tc?.is_component_linked === false || tc?.is_lr_linked === false,
    );
    const componentLinked = freshTestCases.filter(
      (tc) =>
        tc?.is_component_linked !== false && tc?.is_lr_linked !== false,
    );

    pipelineQueueRef.current = componentLinked;

    if (componentUnlinked.length > 0) {
      setTestCasesToComponentLink(componentUnlinked);
      setOpenComponentLinkDialog(true);
    } else {
      runPipelineStep2(componentLinked);
    }
  };

  const testCaseEntityType = isVehicleSystemMode ? "vs" : "vc";
  const testCaseEntityId = isVehicleSystemMode
    ? selectedVSMain[0]?.id
    : selectedVSMain[0]?.vcData?.[0]?.id;

  const getRecommendationVS = (recommendation, fallbackVC = null) => {
    const parentContext =
      recommendation?.parentContext ||
      recommendation?._ai?.targetContext?.parentContext;
    const parent =
      recommendation?.parent || recommendation?._ai?.targetContext?.parent;
    const vsId =
      fallbackVC?.pr_vs_id ||
      fallbackVC?.parent_vs_id ||
      fallbackVC?.vs_id ||
      recommendation?.pr_vs_id ||
      recommendation?.parent_vs_id ||
      parentContext?.parent_id ||
      parentContext?.parent_vs_id ||
      parent?.entityId ||
      parent?.id ||
      selectedVSMain?.[0]?.id ||
      selectedVSMain?.[0]?.vs_id;

    const matchedVS =
      (selectedVSMain || []).find(
        (vs) => Number(vs?.id || vs?.vs_id) === Number(vsId),
      ) || selectedVSMain?.[0];

    const resolvedVsId = vsId || matchedVS?.id || matchedVS?.vs_id;

    return {
      id: resolvedVsId,
      vs_id: resolvedVsId,
      name:
        matchedVS?.name ||
        parentContext?.parent_name ||
        parentContext?.name ||
        parent?.description ||
        parent?.number ||
        "Vehicle System",
      description:
        matchedVS?.description ||
        parentContext?.description ||
        parent?.description ||
        "",
    };
  };

  const syncSelectedVehicleComponent = (vcDetails, recommendation = null) => {
    const vcId = vcDetails?.id || vcDetails?.vc_id;
    if (!vcId) return;

    const vsDetails = getRecommendationVS(recommendation, vcDetails);

    const normalizedVCDetails = {
      ...vcDetails,
      id: vcId,
      vc_id: vcId,
      name:
        vcDetails?.name ||
        vcDetails?.component_name ||
        vcDetails?.description ||
        `VC-${vcId}`,
      component_number:
        vcDetails?.component_number ||
        vcDetails?.number ||
        String(vcId),
    };

    const addVCToVS = (prev = []) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      let existingVS = safePrev.find(
        (vs) =>
          Number(vs?.id || vs?.vs_id) === Number(vsDetails?.id || vsDetails?.vs_id),
      );

      if (!existingVS && safePrev.length > 0) {
        existingVS = safePrev[0];
      }

      if (existingVS) {
        const existingVCData = Array.isArray(existingVS.vcData)
          ? existingVS.vcData
          : [];
        const vcExists = existingVCData.some(
          (vc) => Number(vc?.id || vc?.vc_id) === Number(vcId),
        );

        const updatedVCData = vcExists
          ? existingVCData.map((vc) =>
              Number(vc?.id || vc?.vc_id) === Number(vcId)
                ? { ...vc, ...normalizedVCDetails }
                : vc,
            )
          : [...existingVCData, normalizedVCDetails];

        return safePrev.map((vs) =>
          Number(vs?.id || vs?.vs_id) === Number(existingVS?.id || existingVS?.vs_id)
            ? {
                ...vs,
                vcData: updatedVCData,
              }
            : vs,
        );
      }

      return [
        ...safePrev,
        {
          ...(vsDetails || {
            id: normalizedVCDetails.pr_vs_id,
            vs_id: normalizedVCDetails.pr_vs_id,
            name: "Vehicle System",
          }),
          vcData: [normalizedVCDetails],
        },
      ];
    };

    setSelectedVSMain(addVCToVS);
    setSelectedVSForDisplay(addVCToVS);
  };

  const handleSmartMatchRequest = async (componentDetails, tag) => {
    setDisableSmartMatch(true);
    setCloseAiDialogOnAdd(false);

    const rawItems = Array.isArray(componentDetails)
      ? componentDetails
      : componentDetails
      ? [componentDetails]
      : [];

    const primaryVehicleEntity = getPrimarySelectedVehicleEntity();
    const fallbackItems =
      rawItems.length > 0
        ? rawItems
        : tag === ENTITY.TYPE.LEGAL
        ? (isVehicleSystemMode ? selectedVSMain : selectedVCMain) || []
        : selectedLegalMain || [];

    const descriptions = fallbackItems
      .map((item) => item?.description || item?.name)
      .filter(Boolean);

    const componentName =
      descriptions.length > 0
        ? descriptions.join(", ")
        : primaryVehicleEntity?.description || primaryVehicleEntity?.name;

    if (componentName?.length > 0) {
      setComponentToBeMatched([componentName]);
    } else {
      setComponentToBeMatched(["smart-match"]);
    }

    const sourceType =
      tag === ENTITY.TYPE.LEGAL ? vehicleEntityType : ENTITY.TYPE.LEGAL;

    const selectedVehicleScopes = (
      (isVehicleSystemMode ? selectedVSMain : selectedVCMain) || []
    )
      .map((vehicleEntity) => ({
        entityType: vehicleEntityType,
        entityId: Number(
          vehicleEntity?.id || vehicleEntity?.vs_id || vehicleEntity?.vc_id,
        ),
      }))
      .filter((scopeEntity) => Number.isFinite(scopeEntity.entityId));

    const source =
      sourceType === vehicleEntityType
        ? fallbackItems
            .map((vehicleEntity) => ({
              entityType: vehicleEntityType,
              entityId: String(
                vehicleEntity?.id ||
                  vehicleEntity?.vs_id ||
                  vehicleEntity?.vc_id,
              ),
            }))
            .filter(
              (s) =>
                s.entityId &&
                s.entityId !== "undefined" &&
                s.entityId !== "null",
            )
        : fallbackItems
            .map((item) => ({
              entityType: sourceType,
              entityId: String(item?.lr_id || item?.id),
            }))
            .filter(
              (s) =>
                s.entityId &&
                s.entityId !== "undefined" &&
                s.entityId !== "null",
            );

    const shouldUseVehicleScope = tag === ENTITY.TYPE.TEST_CASE;

    setshowSmartMatchDialog(true);
    setOpenAISuggestion(true);
    setSelectedEntity({
      source,
      targetEntityTypes: [tag],
      topK: 20,
      minConfidence: 0.1,
      description: componentName,
      scope: shouldUseVehicleScope
        ? {
            projectId: Number(selectedProjectDetails?.project_id),
            variantId: Number(selectedVariantDetails?.variant_id),
            scopeEntities: selectedVehicleScopes,
          }
        : {
            projectId: Number(selectedProjectDetails?.project_id),
            variantId: Number(selectedVariantDetails?.variant_id),
          },
    });
    setDisableSmartMatch(false);
  };

  const fetchVehicleComponentRecommendationContext = async (recommendation) => {
    const vcId = recommendation?.id || recommendation?.vc_id;
    if (!vcId) return;

    const vsId =
      recommendation?.pr_vs_id ||
      recommendation?.parent_vs_id ||
      recommendation?.parentContext?.parent_vs_id ||
      recommendation?.parentContext?.parent_id ||
      recommendation?.parent?.entityId ||
      recommendation?.parent?.id ||
      (Array.isArray(recommendation?.parentEntity) &&
        recommendation.parentEntity[0]?.parentEntityId) ||
      selectedVSMain?.[0]?.id ||
      selectedVSMain?.[0]?.vs_id;

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...labCar.get_vehicle_components_all,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedVariantDetails?.variant_id),
            ...(vsId ? { vs_id: Number(vsId) } : {}),
            vc_id: Number(vcId),
            id: Number(vcId),
            page_number: 1,
            page_size: 50,
          },
          show_toast: false,
          ignore_app_loading_spinner: true,
        }),
      ),
    );

    let vcData = null;
    if (!resp?.error && Array.isArray(resp?.data)) {
      vcData =
        resp.data.find(
          (item) =>
            Number(item?.id) === Number(vcId) ||
            Number(item?.vc_id) === Number(vcId),
        ) || resp.data[0];
    }

    if (!vcData) {
      const fallbackResp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.get_vehicle_components_all,
            params: {
              project_id: Number(selectedProjectDetails?.project_id),
              variant_id: Number(selectedVariantDetails?.variant_id),
              vc_id: Number(vcId),
              id: Number(vcId),
            },
            show_toast: false,
            ignore_app_loading_spinner: true,
          }),
        ),
      );
      if (!fallbackResp?.error && Array.isArray(fallbackResp?.data)) {
        vcData =
          fallbackResp.data.find(
            (item) =>
              Number(item?.id) === Number(vcId) ||
              Number(item?.vc_id) === Number(vcId),
          ) || fallbackResp.data[0];
      }
    }

    const fullVC = {
      ...vcData,
      ...recommendation,
      ...(vcData || {}),
      id: vcId,
      vc_id: vcId,
      name:
        vcData?.name ||
        vcData?.component_name ||
        recommendation?.name ||
        recommendation?.component_name ||
        recommendation?.description ||
        `VC-${vcId}`,
      component_number:
        vcData?.component_number ||
        recommendation?.component_number ||
        recommendation?.number ||
        String(vcId),
      description:
        vcData?.description ||
        recommendation?.description ||
        "",
      pr_vs_id:
        recommendation?.pr_vs_id ||
        recommendation?.parent_vs_id ||
        recommendation?.parentContext?.parent_id ||
        recommendation?.parentContext?.parent_vs_id ||
        recommendation?.parent?.entityId ||
        recommendation?.parent?.id ||
        vcData?.pr_vs_id ||
        vsId,
      parent_vs_id:
        recommendation?.parent_vs_id ||
        recommendation?.pr_vs_id ||
        vsId,
      suppliers:
        vcData?.suppliers ||
        vcData?.supplier_name ||
        recommendation?.suppliers ||
        recommendation?.supplier_name ||
        [],
      _recommendation: recommendation,
    };

    setPendingVehicleComponent(fullVC);
    setPendingTargetType("vc");
    const options = getSuppliersList(fullVC);
    setSelectedSupplier(options.length === 1 ? options[0].value : "");
    setOpenSupplierDialog(true);
  };

  const handleVehicleComponentAddClick = (recommendation) => {
    void fetchVehicleComponentRecommendationContext(recommendation);
  };

  const fetchLegalRecommendationContext = async (params) => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          https_method: "GET",
          ...legalRequirements.get_legal_requirements_details_new,
          params,
          show_toast: false,
          ignore_app_loading_spinner: true,
        }),
      ),
    );

    if (!resp.error && resp.data && resp.data.length > 0) {
      const legalRequirementData = resp.data[0];
      setSelectedLegalMain((prev) => {
        const current = prev || [];
        const exists = current.some(
          (item) => item?.lr_id === legalRequirementData?.lr_id,
        );
        const next = exists ? current : [...current, legalRequirementData];
        const allTechUsers = next.flatMap((lr) =>
          (lr?.tech_service_users || []).map((u) => ({
            ...u,
            chapter: lr?.chapter,
          })),
        );
        setSelectedLegalTechUserDetails(removeDuplicates(allTechUsers));
        return next;
      });
      setSelectedLegalForDisplay((prev) => {
        const current = prev || [];
        const exists = current.some(
          (item) => item?.lr_id === legalRequirementData?.lr_id,
        );
        return exists ? current : [...current, legalRequirementData];
      });
    }
  };

  const handleLegalAddClick = (recommendation) => {
    void fetchLegalRecommendationContext({ lr_id: recommendation.lr_id });
  };

  const fetchTestCaseRecommendationContext = async (params, recommendation) => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          https_method: "GET",
          ...testCasesActions.get_test_cases,
          params,
          show_toast: false,
          ignore_app_loading_spinner: true,
        }),
      ),
    );

    if (!resp.error && resp.data && resp.data.length > 0) {
      const testCaseData = resp.data[0];
      const mergedTestCase = {
        ...testCaseData,
        ...recommendation,
        ...testCaseData,
        is_component_linked:
          recommendation?.is_component_linked !== undefined
            ? recommendation.is_component_linked
            : testCaseData?.is_component_linked,
        is_lr_linked:
          recommendation?.is_lr_linked !== undefined
            ? recommendation.is_lr_linked
            : testCaseData?.is_lr_linked,
        is_campaign_linked:
          recommendation?.is_campaign_linked !== undefined
            ? recommendation.is_campaign_linked
            : testCaseData?.is_campaign_linked,
      };
      handleSelectedTestCases([mergedTestCase]);
    } else if (recommendation || params) {
      handleSelectedTestCases([recommendation || params]);
    }
  };

  const handleTestCaseAddClick = (recommendation) => {
    void fetchTestCaseRecommendationContext(
      {
        manual_test_case_id: recommendation.manual_test_case_id,
      },
      recommendation,
    );
  };

  const handleLinkSuggestions = async (recommendation) => {
    if (!recommendation) return;
    const recType = String(
      recommendation.type ||
      recommendation.entityType ||
      recommendation.targetContext?.entityType ||
      "",
    ).toUpperCase();

    if (recType === vehicleEntityType || (isVehicleSystemMode && recType === "VS")) {
      if (isVehicleSystemMode) {
        const vsId = recommendation?.id || recommendation?.vs_id;
        const resp = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...labCar.get_vehicle_systems,
              params: {
                project_id: Number(selectedProjectDetails?.project_id),
                variant_id: Number(selectedVariantDetails?.variant_id),
                id: Number(vsId),
                vs_id: Number(vsId),
                page_number: 1,
                page_size: 1,
              },
              show_toast: false,
              ignore_app_loading_spinner: true,
            }),
          ),
        );
        const vsData = !resp.error && resp.data?.length ? resp.data[0] : null;
        const fullVS = {
          ...vsData,
          ...recommendation,
          ...(vsData || {}),
          id: vsId,
          vs_id: vsId,
          suppliers:
            vsData?.suppliers ||
            vsData?.supplier_name ||
            recommendation?.suppliers ||
            recommendation?.supplier_name ||
            [],
        };
        setPendingVehicleComponent(fullVS);
        setPendingTargetType("vs");
        const options = getSuppliersList(fullVS);
        setSelectedSupplier(options.length === 1 ? options[0].value : "");
        setOpenSupplierDialog(true);
      } else {
        handleVehicleComponentAddClick(recommendation);
      }
    } else if (recType === "VC" || recType === "VEHICLE_COMPONENT") {
      handleVehicleComponentAddClick(recommendation);
    } else if (recType === "LEGAL_REQUIREMENT" || recType === "LEGAL") {
      handleLegalAddClick(recommendation);
    } else if (recType === "TEST_CASE") {
      handleTestCaseAddClick(recommendation);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...userManagementApis.get_users_list_pa,
            show_toast: false,
            params: { page_number: 1, page_size: 500 },
          }),
        ),
      );
      setAllUsersList(resp.data);
    };
    void fetchUsers();
  }, [dispatch]);

  useEffect(() => {
    const fetchAdminSuppliers = async () => {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...adminSettingsAPI.get_supplier_details,
            params: { page_number: 1, page_size: 100 },
            show_toast: false,
            ignore_app_loading_spinner: true,
          }),
        ),
      );
      const list =
        resp?.data?.ecu_suppliers ??
        resp?.data ??
        resp?.response?.data ??
        [];
      if (Array.isArray(list) && list.length > 0) {
        setEcuSuppliersList(list);
      }
      if (!adminSettingsDetails?.adminSettings?.data?.ecu_suppliers?.length) {
        void dispatch(
          HandleApiActions({
            ...adminSettingsAPI.get_admin_settings,
            show_toast: false,
            ignore_app_loading_spinner: true,
          }),
        );
      }
    };
    void fetchAdminSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    setZoneList(selectedProjectDetails?.project_zones);
    setIsZonalProject(selectedProjectDetails?.is_zonal);
  }, [selectedProjectDetails]);

  useEffect(() => {
    clearForm();
    setSavedLinkages([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectDetails, selectedVariantDetails]);

  useEffect(() => {
    let linkedLRIds = [];
    const linkedLRCounts = new Map();
    const vcList = [];

    const processVS = (vs) => {
      if (isVehicleSystemMode) {
        const uniqueLegalIds = getVehicleSystemLinkedLegalIds(vs);
        for (const lr of uniqueLegalIds) {
          linkedLRCounts.set(lr, (linkedLRCounts.get(lr) || 0) + 1);
        }
      }

      const vehicleComponents = Array.isArray(vs?.vcData) ? vs.vcData : [];
      for (const vc of vehicleComponents) {
        vcList.push(vc);
        if (!isVehicleSystemMode) {
          for (const lr of vc.legal_req_ids || []) {
            linkedLRIds.push(Number(lr));
          }
        }
      }
    };

    for (const vs of selectedVSMain) {
      processVS(vs);
    }

    if (isVehicleSystemMode) {
      linkedLRIds = Array.from(linkedLRCounts.entries())
        .filter(([, linkedCount]) => linkedCount === selectedVSMain.length)
        .map(([lrId]) => lrId);
    }
    setSelectedVCMain(vcList);
    setDisabledLegalRequirement(linkedLRIds);
  }, [selectedVSMain, isVehicleSystemMode]);

  useEffect(() => {
    if (selectedSmartMatchTestCaseSuggestions.length > 0) {
      setSelectedTestCaseMain(selectedSmartMatchTestCaseSuggestions);
      setSelectedTestCaseForDisplay(selectedSmartMatchTestCaseSuggestions);
    }
  }, [selectedSmartMatchTestCaseSuggestions]);

  useEffect(() => {
    setSelectedTestCaseForDisplay([]);
    setSelectedTestCaseMain([]);
    setSelectedSmartMatchTestCaseSuggestions([]);
  }, [selectedVSMain, selectedLegalMain]);

  const getDisabledVSListForLR = useCallback(
    async ({ lrId }) => {
      if (!lrId) {
        setDisabledVSComponents([]);
        return;
      }

      if (
        !selectedProjectDetails?.project_id ||
        !selectedVariantDetails?.variant_id
      ) {
        setDisabledVSComponents([]);
        return;
      }

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...softwareIntersectionAPI.get_vs_for_legal_requirements,
            params: {
              project_id: Number(selectedProjectDetails?.project_id),
              variant_id: Number(selectedVariantDetails?.variant_id),
              lr_id: Number(lrId),
            },
            show_toast: false,
            ignore_app_loading_spinner: true,
          }),
        ),
      );

      if (resp.error) {
        setDisabledVSComponents([]);
        return;
      }

      const vsIds = resp?.data?.[0]?.vs_ids || [];
      const normalizedIds = vsIds
        .map(Number)
        .filter((id) => Number.isFinite(id));
      setDisabledVSComponents(normalizedIds);
    },
    [dispatch, selectedProjectDetails?.project_id, selectedVariantDetails?.variant_id],
  );

  useEffect(() => {
    if (selectedLegalMain && selectedLegalMain.length > 0) {
      getDisabledVSListForLR({ lrId: selectedLegalMain[0].lr_id }).catch(console.error);
    } else {
      setDisabledVSComponents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLegalMain, selectedProjectDetails, selectedVariantDetails]);

  const handleVSMainChange = (checked, item) => {
    if (checked) {
      setSelectedVSMain((prev) => [...prev, item]);
      setSelectedVSForDisplay((prev) => [...prev, item]);
    } else {
      setSelectedVSMain((prev) => prev.filter((vs) => vs.id !== item.id));
      setSelectedVSForDisplay((prev) => prev.filter((vs) => vs.id !== item.id));
    }
  };

  const getUserWithCapability = (u, usersList = []) => {
    if (!u) return undefined;
    const matched = (usersList || []).find(
      (au) => String(au?.user_id) === String(u?.user_id),
    );
    const capCode =
      matched?.role_details?.capability_code ||
      matched?.capability_code ||
      u?.role_details?.capability_code ||
      u?.capability_code;
    const displayName =
      u?.name ||
      u?.fullname ||
      matched?.fullname ||
      matched?.name ||
      u?.email ||
      matched?.email ||
      "";
    return {
      ...matched,
      ...u,
      name: displayName,
      fullname: displayName,
      capability_code: capCode,
    };
  };

  const insertCapabilityCodeToUserList = (userDetails, usersList = allUsersList) => {
    if (!userDetails || !Array.isArray(userDetails)) return [];
    return userDetails
      .map((u) => getUserWithCapability(u, usersList))
      .filter(Boolean);
  };

  const normalizeSupplierOptions = (list) => {
    if (!list) return [];
    const arr = Array.isArray(list) ? list : [list];
    return arr
      .map((opt) => {
        if (!opt) return null;
        if (typeof opt === "string") {
          const trimmed = opt.trim();
          return trimmed ? { value: trimmed, label: trimmed } : null;
        }
        const value =
          opt.value ?? opt.name ?? opt.supplier_name ?? opt.id ?? opt.label;
        const label =
          opt.label ?? opt.name ?? opt.supplier_name ?? opt.value ?? opt.id;
        if (value == null || label == null) return null;
        const strVal = String(value).trim();
        const strLabel = String(label).trim();
        return strVal ? { value: strVal, label: strLabel || strVal } : null;
      })
      .filter(Boolean);
  };

  const getSuppliersList = (component) => {
    if (!component) return [];
    const rawComponentSuppliers =
      component.suppliers ||
      component.supplier_name ||
      component.supplier ||
      component.vehicle_component_details?.suppliers ||
      component.vehicle_component_details?.supplier_name ||
      component.vc_details?.suppliers ||
      component._recommendation?.suppliers ||
      component._recommendation?.supplier_name ||
      component._ai?.suppliers ||
      component._ai?.targetContext?.suppliers;

    const componentOptions = normalizeSupplierOptions(rawComponentSuppliers);
    if (componentOptions.length > 0) {
      return componentOptions;
    }

    // Fallback 1: Parent VS selected_supplier or suppliers
    const parentVsId =
      component.pr_vs_id ||
      component.parent_vs_id ||
      component.parentContext?.parent_vs_id ||
      component.parentContext?.parent_id;
    const parentVS = (selectedVSMain || []).find(
      (vs) => Number(vs.id || vs.vs_id) === Number(parentVsId),
    );
    const parentOptions = [];
    if (parentVS?.selected_supplier) {
      parentOptions.push({
        label: parentVS.selected_supplier,
        value: parentVS.selected_supplier,
      });
    }

    // Fallback 2: ECU suppliers from admin settings
    const storeSuppliers =
      adminSettingsDetails?.adminSettings?.data?.ecu_suppliers || [];
    const ecuOptions = normalizeSupplierOptions(
      ecuSuppliersList.length > 0 ? ecuSuppliersList : storeSuppliers,
    );

    // Fallback 3: Suppliers from allUsersList
    const userSuppliers = (allUsersList || [])
      .filter(
        (u) =>
          u?.role_details?.capability_code === CAPABILITY_CODE.supplier ||
          u?.capability_code === CAPABILITY_CODE.supplier ||
          u?.role_details?.role_name?.toLowerCase().includes("supplier"),
      )
      .map((u) => ({
        label: u.name || u.fullname || u.email || "Supplier",
        value: u.name || u.fullname || String(u.user_id),
      }));

    const combined = [...parentOptions, ...ecuOptions, ...userSuppliers];
    const seen = new Set();
    const uniqueOptions = [];
    for (const opt of combined) {
      if (opt?.value && !seen.has(opt.value)) {
        seen.add(opt.value);
        uniqueOptions.push(opt);
      }
    }
    return uniqueOptions;
  };

  const extractUsersFromVSData = (data = []) => {
    const allOwners = [];
    const allCoOwners = [];
    const allEditors = [];
    const allViewers = [];

    for (const item of data || []) {
      if (!item) continue;
      if (isVehicleSystemMode) {
        const compName = item.description || item.name || "";
        const attachName = (u) => ({ ...u, component_name: compName });
        const owners = insertCapabilityCodeToUserList(
          item.owner_details ?? [],
          allUsersList,
        );
        const coOwners = insertCapabilityCodeToUserList(
          item.co_owner_details ?? item.co_owners ?? [],
          allUsersList,
        );
        const editors = insertCapabilityCodeToUserList(
          item.edit_user_details ?? [],
          allUsersList,
        );
        const viewers = insertCapabilityCodeToUserList(
          item.view_user_details ?? [],
          allUsersList,
        );
        allOwners.push(...owners.map(attachName));
        allCoOwners.push(...coOwners.map(attachName));
        allEditors.push(...editors.map(attachName));
        allViewers.push(...viewers.map(attachName));

        const vcData = Array.isArray(item.vcData) ? item.vcData : [];
        for (const vc of vcData) {
          if (!vc) continue;
          const vcCompName = vc.component_number || vc.name || "";
          const attachVcName = (u) => ({ ...u, component_name: vcCompName });
          const vcOwners = insertCapabilityCodeToUserList(
            vc.owner_details ?? [],
            allUsersList,
          );
          const vcCoOwners = insertCapabilityCodeToUserList(
            vc.co_owner_details ?? vc.co_owners ?? [],
            allUsersList,
          );
          const vcEditors = insertCapabilityCodeToUserList(
            vc.edit_user_details ?? [],
            allUsersList,
          );
          const vcViewers = insertCapabilityCodeToUserList(
            vc.view_user_details ?? [],
            allUsersList,
          );
          allOwners.push(...vcOwners.map(attachVcName));
          allCoOwners.push(...vcCoOwners.map(attachVcName));
          allEditors.push(...vcEditors.map(attachVcName));
          allViewers.push(...vcViewers.map(attachVcName));
        }
      } else {
        const vcData = Array.isArray(item.vcData) ? item.vcData : [];
        for (const vc of vcData) {
          if (!vc) continue;
          const compName = vc.component_number || vc.name || "";
          const attachName = (u) => ({ ...u, component_name: compName });
          const owners = insertCapabilityCodeToUserList(
            vc.owner_details ?? [],
            allUsersList,
          );
          const coOwners = insertCapabilityCodeToUserList(
            vc.co_owner_details ?? vc.co_owners ?? [],
            allUsersList,
          );
          const editors = insertCapabilityCodeToUserList(
            vc.edit_user_details ?? [],
            allUsersList,
          );
          const viewers = insertCapabilityCodeToUserList(
            vc.view_user_details ?? [],
            allUsersList,
          );
          allOwners.push(...owners.map(attachName));
          allCoOwners.push(...coOwners.map(attachName));
          allEditors.push(...editors.map(attachName));
          allViewers.push(...viewers.map(attachName));
        }
      }
    }

    const requestors = removeDuplicates([
      ...removeDuplicates(allOwners),
      ...removeDuplicates(allCoOwners),
    ]).filter((ele) => ele?.capability_code !== CAPABILITY_CODE.supplier);

    const suppliers = removeDuplicates([
      ...removeDuplicates(allCoOwners),
      ...removeDuplicates(allEditors),
      ...removeDuplicates(allViewers),
    ]).filter((ele) => ele?.capability_code === CAPABILITY_CODE.supplier);

    return { requestors, suppliers };
  };

  const handleAddSupplier = () => {
    if (pendingVehicleComponent) {
      const supplierValue = selectedSupplier || "";
      const componentWithSupplier = {
        ...pendingVehicleComponent,
        selected_supplier: supplierValue,
      };
      if (pendingTargetType === "vs") {
        setSelectedVSMain((prev) => {
          const current = prev || [];
          const exists = current.some(
            (item) => (item?.id || item?.vs_id) === componentWithSupplier.id,
          );
          return exists
            ? current.map((item) =>
                (item?.id || item?.vs_id) === componentWithSupplier.id
                  ? {
                      ...item,
                      ...componentWithSupplier,
                      selected_supplier: supplierValue,
                    }
                  : item,
              )
            : [...current, componentWithSupplier];
        });
        setSelectedVSForDisplay((prev) => {
          const current = prev || [];
          const exists = current.some(
            (item) => (item?.id || item?.vs_id) === componentWithSupplier.id,
          );
          return exists
            ? current.map((item) =>
                (item?.id || item?.vs_id) === componentWithSupplier.id
                  ? {
                      ...item,
                      ...componentWithSupplier,
                      selected_supplier: supplierValue,
                    }
                  : item,
              )
            : [...current, componentWithSupplier];
        });
      } else if (pendingTargetType === "vc") {
        syncSelectedVehicleComponent(
          componentWithSupplier,
          componentWithSupplier._recommendation,
        );
      }
    }
    resetSupplierDialog();
  };

  const resetSupplierDialog = () => {
    setOpenSupplierDialog(false);
    setPendingVehicleComponent(null);
    setSelectedSupplier("");
    setPendingTargetType("");
  };

  useEffect(() => {
    if (selectedVSMain && selectedVSMain.length > 0) {
      const { requestors, suppliers } = extractUsersFromVSData(selectedVSMain);
      setSelectedComponentRequestor(requestors);
      setSelectedComponentSupplierDetails(suppliers);
    } else {
      setSelectedComponentRequestor([]);
      setSelectedComponentSupplierDetails([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVSMain, allUsersList, isVehicleSystemMode]);

  const removeDuplicates = (arr = []) => {
    if (!Array.isArray(arr)) return [];
    const seen = new Map();
    for (const user of arr) {
      if (!user?.user_id) continue;
      if (seen.has(user.user_id)) {
        const existing = seen.get(user.user_id);
        if (
          user.component_name &&
          existing.component_name &&
          !existing.component_name.includes(user.component_name)
        ) {
          existing.component_name += `, ${user.component_name}`;
        }
      } else {
        seen.set(user.user_id, { ...user });
      }
    }
    return Array.from(seen.values());
  };

  const getUserIds = (users = []) =>
    Array.isArray(users) ? users.map((u) => u?.user_id).filter(Boolean) : [];

  const getVcToLegal = (vcData = []) =>
    vcData
      .map((vc) => {
        if (!vc) return null;

        const uniqueOwners = removeDuplicates(
          insertCapabilityCodeToUserList(
            Array.isArray(vc?.owner_details) ? vc.owner_details : [],
            allUsersList,
          ),
        );
        const uniqueCoOwners = removeDuplicates(
          insertCapabilityCodeToUserList(
            Array.isArray(vc?.co_owner_details) ? vc.co_owner_details : [],
            allUsersList,
          ),
        );
        const uniqueEditors = removeDuplicates(
          insertCapabilityCodeToUserList(
            Array.isArray(vc?.edit_user_details) ? vc.edit_user_details : [],
            allUsersList,
          ),
        );
        const uniqueViewers = removeDuplicates(
          insertCapabilityCodeToUserList(
            Array.isArray(vc?.view_user_details) ? vc.view_user_details : [],
            allUsersList,
          ),
        );

        const requestor = [...uniqueOwners, ...uniqueCoOwners];
        const supplier = [
          ...uniqueCoOwners,
          ...uniqueEditors,
          ...uniqueViewers,
        ].filter((ele) => ele?.capability_code === CAPABILITY_CODE.supplier);

        return {
          vc_id: vc?.id,
          oem_users: getUserIds(requestor),
          suppliers: getUserIds(supplier),
        };
      })
      .filter(Boolean);

  const getTestCaseIds = (testCases = []) =>
    Array.isArray(testCases)
      ? testCases.map((tc) => tc?.test_case_id).filter(Boolean)
      : [];

  const handleLocalSaveForm = () => {
    try {
      if (!Array.isArray(selectedVSMain) || selectedVSMain.length === 0) {
        return;
      }

      const vehicleSystemsForPayload = isVehicleSystemMode
        ? selectedVSMain.filter(
            (item) => !isVehicleSystemLinkedWithLegalRequirement(item),
          )
        : selectedVSMain;

      if (vehicleSystemsForPayload.length === 0) {
        return;
      }

      let linkedVSdetails;
      if (isVehicleSystemMode) {
        const vsNames = vehicleSystemsForPayload
          .map((item) => {
            const vsName = item?.name || item?.description || "-";
            const vcNames =
              Array.isArray(item?.vcData) && item.vcData.length > 0
                ? ` (${item.vcData.map((vc) => vc?.name).filter(Boolean).join(", ")})`
                : "";
            return `${vsName}${vcNames}`;
          })
          .filter(Boolean)
          .join(", ");
        const combinedPayload = {
          vs_id: vehicleSystemsForPayload[0]?.id,
          vs_name: vsNames,
          lr_ids: selectedLegalMain.map((lr) => lr.lr_id),
          test_case_ids: getTestCaseIds(selectedTestCaseMain),
          test_cases: selectedTestCaseMain || [],
          oem_users: Array.isArray(selectedComplianceRequestors)
            ? selectedComplianceRequestors
                .map((user) => user?.value)
                .filter(Boolean)
            : [],
          suppliers: selectedComplianceSupplier.map((user) => user.value),
          tech_service_users: Array.isArray(selectedComplianceReviewers)
            ? selectedComplianceReviewers
                .map((user) => user?.value)
                .filter(Boolean)
            : [],
          description: description || "",
          addOn: vehicleSystemsForPayload,
          ...(showDynamicSelect
            ? { dynamic_form: dynamicFormFilteredData }
            : {}),
        };
        linkedVSdetails = [combinedPayload];
      } else {
        linkedVSdetails = vehicleSystemsForPayload
          .map((item) => {
            if (!item) return null;

            const vcData = Array.isArray(item?.vcData) ? item.vcData : [];
            const vcNames = Array.isArray(item?.vcData)
              ? item.vcData.map((vc) => vc?.name).filter(Boolean).join(", ")
              : "";
            return {
              vs_id: item?.id,
              vs_name: vcNames || item?.name || "-",
              lr_ids: selectedLegalMain.map((lr) => lr.lr_id),
              test_case_ids: getTestCaseIds(selectedTestCaseMain),
              test_cases: selectedTestCaseMain || [],
              oem_users: Array.isArray(selectedComplianceRequestors)
                ? selectedComplianceRequestors
                    .map((user) => user?.value)
                    .filter(Boolean)
                : [],
              suppliers: selectedComplianceSupplier.map((user) => user.value),
              tech_service_users: Array.isArray(selectedComplianceReviewers)
                ? selectedComplianceReviewers
                    .map((user) => user?.value)
                    .filter(Boolean)
                : [],
              description: description || "",
              addOn: item,
              ...(showDynamicSelect
                ? { dynamic_form: dynamicFormFilteredData }
                : {}),
              vc_to_legal: getVcToLegal(vcData),
            };
          })
          .filter(Boolean);
      }

      setSavedLinkages([...savedLinkagesRef.current, ...linkedVSdetails]);
      clearForm();
    } catch (error) {
      console.error(error);
      alert(
        "An error occurred while saving. Please check the console for details.",
      );
    }
  };

  const checkDuplicateIntr = async ({ vsList, lrList }) => {
    if (!vsList?.length || !lrList?.length) return false;

    const lrIds = lrList.map((x) => Number(x.lr_id));
    let duplicateCheck;

    if (isVehicleSystemMode) {
      const entity = vsList.map((vs) => ({
        id: Number(vs.id),
        supplier_name:
          vs.selected_supplier ||
          (Array.isArray(vs.supplier_name)
            ? vs.supplier_name[0]
            : vs.supplier_name) ||
          "",
        child_entity: (vs.vcData || []).map((vc) => ({
          id: Number(vc.id),
          supplier_name:
            vc.selected_supplier ||
            (Array.isArray(vc.supplier_name)
              ? vc.supplier_name[0]
              : vc.supplier_name) ||
            "",
        })),
      }));

      duplicateCheck = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...typeApprovalAPI.check_for_duplicate,
            params: {
              ticket_type: 6,
              child_entity_type: 3,
              entity,
              lr_ids: lrIds,
            },
            show_toast: false,
            ignore_app_loading_spinner: true,
          }),
        ),
      );
    } else {
      const allVCs = vsList.flatMap((vs) => vs.vcData || []);
      const entity = allVCs.map((vc) => ({
        id: Number(vc.id),
        supplier_name:
          vc.selected_supplier ||
          (Array.isArray(vc.supplier_name)
            ? vc.supplier_name[0]
            : vc.supplier_name) ||
          "",
      }));

      duplicateCheck = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...typeApprovalAPI.check_for_duplicate,
            params: {
              ticket_type: 3,
              entity,
              lr_ids: lrIds,
            },
            show_toast: false,
            ignore_app_loading_spinner: true,
          }),
        ),
      );
    }

    if (
      duplicateCheck &&
      !duplicateCheck.error &&
      duplicateCheck.data?.intr_id?.length > 0
    ) {
      const entityLabel = isVehicleSystemMode
        ? "Vehicle system"
        : "Vehicle component";
      setDuplicateAlertMessage(
        `Ticket for selected ${entityLabel} and legal requirement combination already exist in intersection id : ${duplicateCheck.data.intr_id.join(
          ", ",
        )}`,
      );
      setOpenDuplicateAlert(true);
      return true;
    }
    return false;
  };

  const handleEESelectAdd = async (data) => {
    if (selectedLegalMain && selectedLegalMain.length > 0) {
      const isDuplicate = await checkDuplicateIntr({
        vsList: data || [],
        lrList: selectedLegalMain,
      });
      if (isDuplicate) return;
    }

    setSelectedVSMain(data);
    setSelectedVSForDisplay(data);
    setOpenEESelect(false);

    const { requestors, suppliers } = extractUsersFromVSData(data);
    setSelectedComponentRequestor(requestors);
    setSelectedComponentSupplierDetails(suppliers);
  };

  const handleLegalSelectAdd = async (data) => {
    if (selectedVSMain && selectedVSMain.length > 0) {
      const isDuplicate = await checkDuplicateIntr({
        vsList: selectedVSMain,
        lrList: data || [],
      });
      if (isDuplicate) return;
    }

    setSelectedLegalMain(data);
    setSelectedLegalForDisplay(data);
    setOpenLegalSelect(false);
    const allTechUsers = (data || []).flatMap((lr) =>
      (lr?.tech_service_users || []).map((u) => ({
        ...u,
        chapter: lr?.chapter,
      })),
    );
    setSelectedLegalTechUserDetails(removeDuplicates(allTechUsers));
  };

  const handleLegalRequirementCheckboxChange = (checked, item) => {
    const currentSelection = selectedLegalMain || [];
    let newSelectedLegal;
    if (checked) {
      const alreadyExists = currentSelection.some(
        (selectedItem) => selectedItem?.lr_id === item?.lr_id,
      );
      newSelectedLegal = alreadyExists
        ? currentSelection
        : [...currentSelection, item];
    } else {
      newSelectedLegal = currentSelection.filter(
        (selectedItem) => selectedItem?.lr_id !== item?.lr_id,
      );
    }
    setSelectedLegalMain(newSelectedLegal);
    const allTechUsers = (newSelectedLegal || []).flatMap((lr) =>
      (lr?.tech_service_users || []).map((u) => ({
        ...u,
        chapter: lr?.chapter,
      })),
    );
    setSelectedLegalTechUserDetails(removeDuplicates(allTechUsers));
  };

  const handleTestCaseCheckboxChange = (checked, item) => {
    const currentSelection = selectedTestCaseMain || [];
    if (checked && item?.is_campaign_linked === false) {
      openCampaignDialogForTestCases([item]);
      return;
    }
    let newSelectedTestCase;
    if (checked) {
      const alreadyExists = currentSelection.some(
        (selectedItem) => selectedItem?.test_case_id === item?.test_case_id,
      );
      newSelectedTestCase = alreadyExists
        ? currentSelection
        : [...currentSelection, item];
    } else {
      newSelectedTestCase = currentSelection.filter(
        (selectedItem) => selectedItem?.test_case_id !== item?.test_case_id,
      );
    }
    setSelectedTestCaseMain(newSelectedTestCase);
    setSelectedTestCaseForDisplay(newSelectedTestCase);
  };

  const handleDisableLrRadioButton = (item) => {
    const eeExists = savedLinkages?.find(
      (linkage) => linkage.id === selectedVCMain?.id,
    );
    return eeExists
      ? eeExists.legal?.some((legal) => legal.lr_id === item.lr_id)
      : false;
  };

  const handleUnlinkSuggestions = (item) => {
    const type = item?.type || item?.raw?.targetContext?.entityType;
    const entityId = item?.vc_id ?? item?.vs_id ?? item?.id ?? item?.lr_id ?? item?.test_case_id ?? item?.tc_id;

    if (type === "VC" || item?.vc_id) {
      const vcId = item?.vc_id || item?.id || entityId;
      setSelectedVSMain((prev) =>
        (prev || [])
          .map((vs) => ({
            ...vs,
            vcData: (vs?.vcData || []).filter(
              (vc) => String(vc?.id || vc?.vc_id) !== String(vcId),
            ),
          }))
          .filter((vs) => isVehicleSystemMode || (vs?.vcData && vs.vcData.length > 0)),
      );
      setSelectedVSForDisplay((prev) =>
        (prev || [])
          .map((vs) => ({
            ...vs,
            vcData: (vs?.vcData || []).filter(
              (vc) => String(vc?.id || vc?.vc_id) !== String(vcId),
            ),
          }))
          .filter((vs) => isVehicleSystemMode || (vs?.vcData && vs.vcData.length > 0)),
      );
      setSelectedVCMain((prev) =>
        (prev || []).filter((c) => String(c?.id || c?.vc_id) !== String(vcId)),
      );
    }
    if (type === "VS" || item?.vs_id) {
      const vsId = item?.vs_id || item?.id || entityId;
      setSelectedVSMain((prev) =>
        (prev || []).filter((vs) => String(vs?.id || vs?.vs_id) !== String(vsId)),
      );
      setSelectedVSForDisplay((prev) =>
        (prev || []).filter((vs) => String(vs?.id || vs?.vs_id) !== String(vsId)),
      );
    }
    if (type === "LEGAL_REQUIREMENT" || item?.lr_id) {
      const lrId = item?.lr_id || entityId;
      setSelectedLegalMain((prev) => {
        const next = (prev || []).filter(
          (c) => String(c?.lr_id) !== String(lrId),
        );
        const allTechUsers = next.flatMap((lr) =>
          (lr?.tech_service_users || []).map((u) => ({
            ...u,
            chapter: lr?.chapter,
          })),
        );
        setSelectedLegalTechUserDetails(removeDuplicates(allTechUsers));
        return next;
      });
      setSelectedLegalForDisplay((prev) =>
        (prev || []).filter((c) => String(c?.lr_id) !== String(lrId)),
      );
    }
    if (type === "TEST_CASE" || item?.test_case_id || item?.tc_id) {
      const tcId = item?.test_case_id || item?.tc_id || entityId;
      const tcNumber = item?.manual_test_case_id || item?.req_number;
      setSelectedTestCaseMain((prev) =>
        (prev || []).filter(
          (c) =>
            String(c?.test_case_id) !== String(tcId) &&
            (!c?.original_test_case_id || String(c?.original_test_case_id) !== String(tcId)) &&
            (!tcNumber || String(c?.manual_test_case_id) !== String(tcNumber)),
        ),
      );
      setSelectedTestCaseForDisplay((prev) =>
        (prev || []).filter(
          (c) =>
            String(c?.test_case_id) !== String(tcId) &&
            (!c?.original_test_case_id || String(c?.original_test_case_id) !== String(tcId)) &&
            (!tcNumber || String(c?.manual_test_case_id) !== String(tcNumber)),
        ),
      );
      setSelectedSmartMatchTestCaseSuggestions((prev) =>
        (prev || []).filter(
          (c) =>
            String(c?.test_case_id) !== String(tcId) &&
            (!c?.original_test_case_id || String(c?.original_test_case_id) !== String(tcId)) &&
            (!tcNumber || String(c?.manual_test_case_id) !== String(tcNumber)),
        ),
      );
    }
  };

  return (
    <Box>
      <Grid container>
        <Grid item sm={12} sx={{ borderBottom: "1px solid black" }}>
          <Box sx={{ display: "flex" }}>
            <Typography sx={{ ...componentStyle.font.cardHeadder }}>
              Select Entities and Fill in the Details
            </Typography>
          </Box>
        </Grid>

        <Grid item sm={12} sx={{ pt: 1 }}>
          <RaiseTicketTemplate
            leftContent={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  pr: 0.5,
                }}
              >
                <Box>
                  <SelectTemplate
                    heading={
                      isVehicleSystemMode
                        ? "Select Vehicle System"
                        : "Select Vehicle Component"
                    }
                    isRequired={true}
                    onClickAdd={() => {
                      setOpenEESelect(true);
                    }}
                    enableEdit={selectedVSForDisplay.length}
                    disableSmartMatch={disableSmartMatch}
                    showSmartMatch={selectedLegalMain && selectedLegalMain.length > 0}
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(
                        selectedLegalMain,
                        vehicleEntityType,
                      )
                    }
                    content={
                      selectedVSForDisplay.length ? (
                        <Box sx={{ pl: 2 }}>
                          {selectedVSForDisplay.map((item, index) => {
                            const selectedVehicleSystem = item;
                            const selectedVehicleComponents = Array.isArray(
                              selectedVehicleSystem?.vcData,
                            )
                              ? selectedVehicleSystem.vcData
                              : [];
                            const selectedVehicleComponentNames =
                              selectedVehicleComponents
                                .map((vc) => vc?.name)
                                .filter(Boolean)
                                .join(", ");
                            const selectedVehicleComponentNumbers =
                              selectedVehicleComponents
                                .map((vc) => vc?.component_number)
                                .filter(Boolean)
                                .join(", ");
                            const getCheckedValueForVS = () => {
                              for (const vs of selectedVSMain) {
                                if (vs.id === item.id) {
                                  return true;
                                }
                              }
                              return false;
                            };
                            return (
                              <Box
                                key={item.id || index}
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "start",
                                  position: "relative",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: "10%",
                                  }}
                                >
                                  <CustomCheckBoxWithoutFormControl
                                    value={getCheckedValueForVS()}
                                    onChange={(e) =>
                                      handleVSMainChange(
                                        e.target.checked,
                                        item,
                                      )
                                    }
                                  />
                                </Box>
                                <Box
                                  sx={{
                                    width: "90%",
                                  }}
                                >
                                  <KeyValueDisplayCard
                                    data={[
                                      {
                                        key: "Name",
                                        value: isVehicleSystemMode
                                          ? selectedVehicleSystem?.name || "-"
                                          : selectedVehicleComponentNames ||
                                            selectedVehicleSystem?.name ||
                                            "-",
                                        style: { fontWeight: 600 },
                                      },
                                      {
                                        key: "Description",
                                        value:
                                          selectedVehicleSystem?.description ||
                                          "-",
                                      },
                                      {
                                        key: "Vehicle Components",
                                        value: isVehicleSystemMode
                                          ? selectedVehicleComponentNames || "-"
                                          : selectedVehicleComponentNumbers ||
                                            "-",
                                      },
                                    ].filter(
                                      (field) =>
                                        (!isVehicleSystemMode ||
                                          (field.key === "Vehicle Components" &&
                                            Boolean(selectedVehicleComponentNames))) ||
                                        field.key !== "Vehicle Components",
                                    )}
                                  />
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <></>
                      )
                    }
                  />
                </Box>

                <Box>
                  <SelectTemplate
                    heading={"Select Legal Requirement"}
                    isRequired={true}
                    onClickAdd={() => {
                      setOpenLegalSelect(true);
                    }}
                    enableEdit={
                      selectedLegalForDisplay &&
                      selectedLegalForDisplay.length > 0
                    }
                    disableSmartMatch={disableSmartMatch}
                    showSmartMatch={
                      getPrimarySelectedVehicleEntity() !== null
                    }
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(
                        isVehicleSystemMode ? selectedVSMain : selectedVCMain,
                        ENTITY.TYPE.LEGAL,
                      )
                    }
                    content={
                      selectedLegalForDisplay &&
                      selectedLegalForDisplay.length > 0 ? (
                        <Box sx={{ pl: 2, maxHeight: "200px", overflowY: "auto" }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              justifyContent: "left",
                              pl: 1,
                              width: "100%",
                            }}
                          >
                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                fontWeight: 600,
                              }}
                            >
                              Legal Standard:
                            </Typography>
                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                textAlign: "left",
                                width: "100%",
                              }}
                            >
                              {`Name: ${selectedLegalForDisplay[0]?.legal_standard_details?.name || ""}`}
                            </Typography>
                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                textAlign: "left",
                                width: "100%",
                              }}
                            >
                              {`Number: ${selectedLegalForDisplay[0]?.legal_standard_details?.number || ""}`}
                            </Typography>
                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                textAlign: "left",
                                width: "100%",
                              }}
                            >
                              {`Revision: ${selectedLegalForDisplay[0]?.legal_standard_details?.revision || ""}`}
                            </Typography>

                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                fontWeight: 600,
                                mt: 1,
                              }}
                            >
                              Legal Requirements:
                            </Typography>
                            {selectedLegalForDisplay.map((lr, idx) => (
                              <Box
                                key={lr.lr_id || idx}
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "start",
                                  position: "relative",
                                  mb: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: "10%",
                                  }}
                                >
                                  <CustomCheckBoxWithoutFormControl
                                    value={
                                      selectedLegalMain?.some(
                                        (selectedItem) =>
                                          selectedItem?.lr_id === lr?.lr_id,
                                      ) || false
                                    }
                                    onChange={(e) =>
                                      handleLegalRequirementCheckboxChange(
                                        e.target.checked,
                                        lr,
                                      )
                                    }
                                  />
                                </Box>
                                <Box
                                  sx={{
                                    width: "90%",
                                    pl: 1,
                                    borderLeft: "2px solid #ccc",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      ...componentStyle.font.text,
                                      textAlign: "left",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {`Number: ${lr.req_number || ""}`}
                                  </Typography>
                                  <CustomToolTip
                                    length={200}
                                    style={{
                                      ...componentStyle.font.text,
                                      textAlign: "left",
                                      width: "100%",
                                    }}
                                  >
                                    {`Description: ${lr.description || ""}`}
                                  </CustomToolTip>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ) : (
                        <></>
                      )
                    }
                  />
                </Box>

                <Box>
                  <SelectTemplate
                    heading={"Select Test Case"}
                    onClickAdd={() => {
                      setOpenTestCaseSelect(true);
                    }}
                    addButtonDisabled={
                      !selectedLegalMain || selectedLegalMain.length === 0 || selectedVSMain.length === 0
                    }
                    disableSmartMatch={disableSmartMatch}
                    enableEdit={
                      selectedTestCaseForDisplay?.length > 0
                    }
                    showSmartMatch={
                      getPrimarySelectedVehicleEntity() !== null &&
                      selectedLegalMain && selectedLegalMain.length > 0
                    }
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(
                        selectedLegalMain,
                        ENTITY.TYPE.TEST_CASE,
                      )
                    }
                    content={
                      <Box sx={{ pl: 2 }}>
                        {selectedTestCaseForDisplay?.length > 0 &&
                          selectedTestCaseForDisplay.map((item) => (
                            <Box
                              key={item.test_case_id}
                              sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "start",
                                position: "relative",
                              }}
                            >
                              <Box
                                sx={{
                                  width: "10%",
                                }}
                              >
                                <CustomCheckBoxWithoutFormControl
                                  value={
                                    selectedTestCaseMain?.some(
                                      (selectedItem) =>
                                        selectedItem?.test_case_id ===
                                        item?.test_case_id,
                                    ) || false
                                  }
                                  onChange={(e) =>
                                    handleTestCaseCheckboxChange(
                                      e.target.checked,
                                      item,
                                    )
                                  }
                                />
                              </Box>
                              <Box
                                sx={{
                                  width: "60%",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    p: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Test Case ID: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.test_case_id}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Test Case Title: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.name}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Description: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.description}
                                    </Box>
                                  </Typography>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  width: "30%",
                                }}
                              />
                            </Box>
                          ))}
                      </Box>
                    }
                  />
                </Box>
              </Box>
            }
            rightContent={
              <RaiseTicketRightTemplate
                selectedComponentRequestor={selectedComponentRequestor}
                selectedLegalTechUserDetails={selectedLegalTechUserDetails}
                selectedComplianceRequestors={selectedComplianceRequestors}
                setSelectedComplianceRequestor={setSelectedComplianceRequestors}
                selectedComplianceReviewers={selectedComplianceReviewers}
                formType={isVehicleSystemMode ? "vehicle-system" : "vehicle-component"}
                setSelectedComplianceReviewers={setSelectedComplianceReviewers}
                description={description}
                setDescription={setDescription}
                selectedComponentSupplierDetails={
                  selectedComponentSupplierDetails
                }
                selectedComplianceSupplier={selectedComplianceSupplier}
                setSelectedComplianceSupplier={setSelectedComplianceSupplier}
                selectedDynamicForm={selectedDynamicForm}
                setSelectedDynamicForm={setSelectedDynamicForm}
                dynamicFormFilteredData={dynamicFormFilteredData}
                setDynamicFormFilteredData={setDynamicFormFilteredData}
                showDynamicSelect={showDynamicSelect}
                setShowDynamicSelect={setShowDynamicSelect}
                localSubmitDisabled={
                  !selectedVSMain.length ||
                  (!isVehicleSystemMode &&
                    selectedVSMain.some((item) => !item.vcData?.length)) ||
                  !selectedLegalMain ||
                  selectedLegalMain.length === 0 ||
                  description === "" ||
                  description.trim().length < 3 ||
                  selectedComplianceRequestors.length === 0 ||
                  selectedComplianceReviewers.length === 0
                }
                handleLocalSaveForm={handleLocalSaveForm}
                savedLinkageCard={
                  <VehicleSavedLinkageCard
                    savedLinkages={savedLinkages}
                    setSavedLinkages={setSavedLinkages}
                    entityType={"vs"}
                    onRemove={clearForm}
                  />
                }
              />
            }
          />
        </Grid>
      </Grid>

      <DialogIndex
        key={formResetKey}
        openEESelect={openEESelect}
        setOpenEESelect={setOpenEESelect}
        isZonalProject={isZonalProject}
        disabledLegalRequirement={disabledLegalRequirement}
        shouldShowLegalRequirementLinkedInfo={
          shouldShowLegalRequirementLinkedInfo
        }
        setDisabledLegalRequirement={setDisabledLegalRequirement}
        zoneList={zoneList}
        savedLinkages={savedLinkages}
        disabledVSComponents={disabledVSComponents}
        disableLrRadioButton={handleDisableLrRadioButton}
        selectedVSMain={selectedVSMain}
        requireVehicleComponentSelection={!isVehicleSystemMode}
        vehicleDialogTitle={
          isVehicleSystemMode
            ? "Select Vehicle System"
            : "Select Vehicle Component"
        }
        onClickAdd={handleEESelectAdd}
        openLegalSelect={openLegalSelect}
        setOpenLegalSelect={setOpenLegalSelect}
        selectedLegalMain={selectedLegalMain}
        setSelectedLegalMain={setSelectedLegalMain}
        onClickAddLegal={handleLegalSelectAdd}
        openTestCaseSelect={openTestCaseSelect}
        setOpenTestCaseSelect={setOpenTestCaseSelect}
        selectedTextCaseMain={selectedTestCaseMain}
        onClickAddTestCase={(data) => {
          handleSelectedTestCases(data);
          setOpenTestCaseSelect(false);
        }}
        entityType={"vs"}
        entityID={selectedVSMain[0]?.vcData?.[0]?.id}
        testCaseEntityType={testCaseEntityType}
        testCaseEntityId={testCaseEntityId}
      />
      {showSmartMatchDialog && componentToBeMatched?.length > 0 && (
        <AISuggestionDialogContainer
          open={openAISuggestion}
          onClose={() => setOpenAISuggestion(false)}
          entity={selectedEntity}
          onLinkRequest={handleLinkSuggestions}
          onUnlinkRequest={handleUnlinkSuggestions}
          closeDialogOnAdd={closeAiDialogOnAdd}
          selectedComponents={isVehicleSystemMode ? selectedVSMain : selectedVCMain}
          selectedLegals={selectedLegalMain}
          selectedTestCases={selectedTestCaseMain}
        />
      )}
      <TestCaseCampaignLinkDialog
        open={openCampaignLinkDialog}
        testCasesToLink={testCasesToCampaignLink}
        projectId={selectedProjectDetails?.project_id}
        variantId={selectedVariantDetails?.variant_id}
        onClose={clearCampaignLinkFields}
        onSuccess={(linkedTestCases) => {
          clearCampaignLinkFields();
          runPipelineStep3(
            (linkedTestCases || []).map((tc) => ({
              ...tc,
              is_campaign_linked: true,
            })),
          );
        }}
      />
      <TestCaseComponentLinkDialog
        entityType="6"
        open={openComponentLinkDialog}
        testCasesToLink={testCasesToComponentLink}
        selectedEEMain={selectedVCMain}
        selectedLegalMain={selectedLegalMain}
        onClose={() => setOpenComponentLinkDialog(false)}
        onSuccess={(linkedTestCases) => {
          setOpenComponentLinkDialog(false);
          const componentLinkedCases = (linkedTestCases || []).map((tc) => ({
            ...tc,
            is_component_linked: true,
            is_lr_linked: true,
          }));
          const allCasesForStep2 = [
            ...pipelineQueueRef.current,
            ...componentLinkedCases,
          ];
          pipelineQueueRef.current = [];
          runPipelineStep2(allCasesForStep2);
        }}
      />
      <CustomDialogBoxV2
        open={openDuplicateAlert}
        onClose={() => setOpenDuplicateAlert(false)}
        title="Duplicate Ticket Alert"
        hideDefaultActions={true}
        footerSlot={
          <DialogActions sx={{ pr: 3, pl: 3, pb: 2, justifyContent: "flex-end" }}>
            <Button
              onClick={() => setOpenDuplicateAlert(false)}
              variant="contained"
              sx={{
                backgroundColor: "black",
                color: "white",
                "&:hover": {
                  backgroundColor: "#333",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        }
      >
        <Typography>{duplicateAlertMessage}</Typography>
      </CustomDialogBoxV2>
      <CustomDialogBoxV2
        title="Select Supplier"
        open={openSupplierDialog}
        onClose={resetSupplierDialog}
        sx={{ zIndex: 1400 }}
        dialogActions={
          <SelectDialogActionButtons
            enableSubmit={
              Boolean(selectedSupplier) ||
              getSuppliersList(pendingVehicleComponent).length === 0
            }
            onClickAdd={handleAddSupplier}
            onClickCancel={resetSupplierDialog}
          />
        }
      >
        <Box sx={{ p: 2, minWidth: "300px" }}>
          <CustomSelectV2
            label="Select Supplier"
            optionsList={getSuppliersList(pendingVehicleComponent)}
            selectedValue={selectedSupplier}
            handleOnChange={(e) => setSelectedSupplier(e.target.value)}
          />
        </Box>
      </CustomDialogBoxV2>
    </Box>
  );
}

RaiseVehicleTickets.propTypes = {
  savedLinkages: PropTypes.array,
  setSavedLinkages: PropTypes.func,
  entityType: PropTypes.string,
  vehicleTicketMode: PropTypes.string,
};

export default RaiseVehicleTickets;
